import React, { useEffect, useRef, useState } from "react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import html2canvas from "html2canvas";
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend } from "recharts";
import { createRoot } from "react-dom/client";
import logo from "../assets/img/logo.png";
import api from "../api/Api";
import { useLocation } from "react-router-dom";

const RelatorioPDF = () => {
  const gerado = useRef(false);
  const hasDownloadedOnce = useRef(false);

  const [resumoFilas, setResumoFilas] = useState([]);
  const [registrosFilas, setRegistrosFilas] = useState([]);
  const [filasProcessadas, setFilasProcessadas] = useState([]);
  const [dataInicio, setDataInicio] = useState(null);
  const [dataFim, setDataFim] = useState(null);
  const [statusGeracao, setStatusGeracao] = useState("carregando"); // "carregando", "gerando", "concluido"

  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const inicio = params.get("datainicio");
    const fim = params.get("datafim");

    if (inicio && fim) {
      setDataInicio(inicio);
      setDataFim(fim);
    }
  }, [location.search]);

  // Função para processar os registros e agrupar por data e fila
  const processarRegistrosParaGrafico = (registros) => {
    const dadosAgrupados = {};

    registros.forEach(registro => {
      const data = registro.data.split(' ')[0]; // Pega apenas a data (YYYY-MM-DD)
      const fila = registro.fila;
      const status = registro.status;

      if (!dadosAgrupados[fila]) {
        dadosAgrupados[fila] = {};
      }

      if (!dadosAgrupados[fila][data]) {
        dadosAgrupados[fila][data] = {
          atendidas: 0,
          abandonadas: 0,
          naoAtendidas: 0
        };
      }

      // Contabiliza baseado no status
      if (status === 'completed') {
        dadosAgrupados[fila][data].atendidas++;
      } else if (status === 'abandoned') {
        dadosAgrupados[fila][data].abandonadas++;
      } else if (status === 'no_answer') {
        dadosAgrupados[fila][data].naoAtendidas++;
      }
    });

    return dadosAgrupados;
  };

  // Função para formatar dados do gráfico
  const formatarDadosGrafico = (dadosAgrupados, nomeFila) => {
    if (!dadosAgrupados[nomeFila]) return [];

    return Object.entries(dadosAgrupados[nomeFila]).map(([data, valores]) => {
      // Formata a data para DD/MM
      const [ano, mes, dia] = data.split('-');
      const dataFormatada = `${dia}/${mes}`;

      return {
        data: dataFormatada,
        atendidas: valores.atendidas,
        abandonadas: valores.abandonadas,
        naoAtendidas: valores.naoAtendidas
      };
    });
  };

  // Função para calcular métricas de tempo médio entre todas as filas
  const calcularMetricasTempoGerais = (resumoFilas) => {
    if (!resumoFilas.length) return { tempoMedioEspera: "00:00:00", duracaoMaxima: "00:00:00", duracaoMedia: "00:00:00" };

    // Encontrar a duração máxima entre todas as filas
    let duracaoMaxima = "00:00:00";
    
    // Calcular tempo médio de espera (média simples entre as filas)
    let totalEsperaSegundos = 0;
    let totalDuracaoMediaSegundos = 0;
    let filasComTempo = 0;

    resumoFilas.forEach(fila => {
      // Converter tempo médio de espera para segundos
      if (fila.tempo_medio_espera && fila.tempo_medio_espera !== "00:00:00") {
        const [horas, minutos, segundos] = fila.tempo_medio_espera.split(':').map(Number);
        totalEsperaSegundos += horas * 3600 + minutos * 60 + segundos;
        filasComTempo++;
      }

      // Converter duração média para segundos
      if (fila.duracao_media_completed && fila.duracao_media_completed !== "00:00:00") {
        const [horas, minutos, segundos] = fila.duracao_media_completed.split(':').map(Number);
        totalDuracaoMediaSegundos += horas * 3600 + minutos * 60 + segundos;
      }

      // Encontrar duração máxima
      if (fila.duracao_maxima && fila.duracao_maxima > duracaoMaxima) {
        duracaoMaxima = fila.duracao_maxima;
      }
    });

    // Calcular médias
    const tempoMedioEsperaSegundos = filasComTempo > 0 ? totalEsperaSegundos / filasComTempo : 0;
    const duracaoMediaSegundos = resumoFilas.length > 0 ? totalDuracaoMediaSegundos / resumoFilas.length : 0;

    // Converter segundos de volta para HH:MM:SS
    const formatarTempo = (segundos) => {
      const horas = Math.floor(segundos / 3600);
      const minutos = Math.floor((segundos % 3600) / 60);
      const segs = Math.floor(segundos % 60);
      return `${horas.toString().padStart(2, '0')}:${minutos.toString().padStart(2, '0')}:${segs.toString().padStart(2, '0')}`;
    };

    return {
      tempoMedioEspera: formatarTempo(tempoMedioEsperaSegundos),
      duracaoMaxima: duracaoMaxima,
      duracaoMedia: formatarTempo(duracaoMediaSegundos)
    };
  };

  useEffect(() => {
    const carregarDados = async () => {
      if (!dataInicio || !dataFim || hasDownloadedOnce.current) return;

      try {
        setStatusGeracao("carregando");
        const dados = {
          dataInicio: dataInicio,
          dataFim: dataFim,
        };

        const [resumoResponse, registrosResponse] = await Promise.all([
          api.ListResumoFila(dados),
          api.ListRegistoFila(dados)
        ]);

        setResumoFilas(resumoResponse);
        setRegistrosFilas(registrosResponse);
        hasDownloadedOnce.current = true;
      } catch (error) {
        console.error("Erro ao carregar dados da API:", error);
        setStatusGeracao("erro");
      }
    };

    carregarDados();
  }, [dataInicio, dataFim]);

  // Processar os dados quando ambos os endpoints retornarem
  useEffect(() => {
    if (resumoFilas.length > 0 && registrosFilas.length > 0) {
      const dadosAgrupados = processarRegistrosParaGrafico(registrosFilas);
      
      const filasFormatadas = resumoFilas.map(fila => {
        const dadosGrafico = formatarDadosGrafico(dadosAgrupados, fila.fila);

        const metricas = [
          ["Total de Chamadas", fila.total_chamadas?.toString() || "0"],
          ["Chamadas Atendidas", fila.total_atendidas?.toString() || "0"],
          ["Não Atendidas", fila.total_nao_atendidas?.toString() || "0"],
          ["Abandonadas", fila.total_abandonadas?.toString() || "0"],
          ["Tempo Médio de Espera", fila.tempo_medio_espera || "00:00:00"],
          ["Duração Máxima", fila.duracao_maxima || "00:00:00"],
          ["Duração Média", fila.duracao_media_completed || "00:00:00"],
        ];

        return {
          nome: fila.fila,
          metricas,
          dadosGrafico
        };
      });

      setFilasProcessadas(filasFormatadas);
      setStatusGeracao("gerando");
    }
  }, [resumoFilas, registrosFilas]);

  useEffect(() => {
    if (filasProcessadas.length > 0 && resumoFilas.length > 0) {
      if (!gerado.current) {
        gerarPDF();
        gerado.current = true;
      }
    }
  }, [filasProcessadas, resumoFilas]);

  const gerarPDF = async () => {
    setStatusGeracao("gerando");
    
  await new Promise(resolve => setTimeout(resolve, 100));


    const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
    const periodo = `${dataInicio} a ${dataFim}`;
    const dataGeracao = new Date().toLocaleString("pt-PT");

    // Calcular totais gerais
    const totaisGerais = {
      total_chamadas: resumoFilas.reduce((acc, fila) => acc + parseInt(fila.total_chamadas || 0), 0),
      total_atendidas: resumoFilas.reduce((acc, fila) => acc + parseInt(fila.total_atendidas || 0), 0),
      total_nao_atendidas: resumoFilas.reduce((acc, fila) => acc + parseInt(fila.total_nao_atendidas || 0), 0),
      total_abandonadas: resumoFilas.reduce((acc, fila) => acc + parseInt(fila.total_abandonadas || 0), 0),
      total_filas: resumoFilas.length
    };

    // Calcular métricas de tempo gerais
    const metricasTempo = calcularMetricasTempoGerais(resumoFilas);

    // === PÁGINA 1 - RESUMO GERAL ===
    doc.addImage(logo, "PNG", 160, 10, 30, 15);
    doc.setFontSize(10);
    doc.text(`Período: ${periodo}`, 10, 15);
    doc.text(`Gerado em: ${dataGeracao}`, 10, 20);
    doc.setFontSize(16);
    doc.text("Relatório de Chamadas - Resumo Geral", 10, 35);

    const metricas = [
      ["Total de chamadas", totaisGerais.total_chamadas.toString()],
      ["Total de chamadas atendidas", totaisGerais.total_atendidas.toString()],
      ["Total de chamadas não Atendidas", totaisGerais.total_nao_atendidas.toString()],
      ["Total de abandonadas", totaisGerais.total_abandonadas.toString()],
      ["Tempo médio de espera", metricasTempo.tempoMedioEspera],
      ["Duração máxima", metricasTempo.duracaoMaxima],
      ["Duração média", metricasTempo.duracaoMedia],
      ["Filas", totaisGerais.total_filas.toString()],
    ];

    autoTable(doc, {
      startY: 45,
      head: [["Métrica", "Valor"]],
      body: metricas,
      styles: { fontSize: 10, cellPadding: 3 },
      headStyles: { fillColor: [41, 128, 185] },
    });

    let y = doc.lastAutoTable.finalY + 12;
    doc.setFontSize(14);
    doc.text("Definições", 10, y);

    const definicoes = [
      ["Total de Chamadas", "Número total de chamadas recebidas durante um período específico."],
      ["Total de chamadas atendidas", "Número total de chamadas em que um agente atendeu e prestou atendimento ao cliente."],
      ["Total de Chamadas Não Atendidas", "Chamadas que tocaram, mas não foram atendidas por nenhum agente."],
      ["Total de Chamadas Abandonadas", "Chamadas que o cliente desligou antes de ser atendido."],
      ["Tempo Médio de Espera", "Tempo médio que os clientes aguardam na fila antes de serem atendidos."],
      ["Duração Máxima da Chamada", "Tempo da chamada mais longa registada no período analisado."],
      ["Duração Média da Chamada", "Tempo médio das chamadas atendidas pelos agentes."],
      ["Fila", "Conjunto de chamadas organizadas por assunto ou tipo de serviço, onde os clientes aguardam atendimento."],
    ];

    y += 8;
    definicoes.forEach(([titulo, descricao], index) => {
      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      doc.text(titulo, 10, y);
      doc.setFont("helvetica", "normal");
      const text = doc.splitTextToSize(descricao, 180);
      y += 5;
      doc.text(text, 10, y);
      y += 7;
      if (index < definicoes.length - 1) {
        doc.setDrawColor(200);
        doc.line(10, y, 200, y);
        y += 5;
      }
    });

    // === CONTAINER TEMPORÁRIO PARA OS GRÁFICOS ===
    const tempDiv = document.createElement("div");
    tempDiv.style.position = "absolute";
    tempDiv.style.left = "-9999px";
    document.body.appendChild(tempDiv);
    const root = createRoot(tempDiv);

    // === GERAR CADA PÁGINA DE FILA ===
    for (const fila of filasProcessadas) {
      doc.addPage();
      doc.addImage(logo, "PNG", 160, 10, 30, 15);
      doc.setFontSize(10);
      doc.text(`Período: ${periodo}`, 10, 15);
      doc.text(`Gerado em: ${dataGeracao}`, 10, 20);
      doc.setFontSize(16);
      doc.text(fila.nome, 10, 35);

      autoTable(doc, {
        startY: 45,
        head: [["Métrica", "Valor"]],
        body: fila.metricas,
        styles: { fontSize: 10, cellPadding: 3 },
        headStyles: { fillColor: [41, 128, 185] },
      });

      // === TÍTULO DO GRÁFICO ===
      const yGraficoTitulo = doc.lastAutoTable.finalY + 15;
      doc.setFontSize(13);
      doc.setFont("helvetica", "bold");
      doc.text("Distribuição das Chamadas por Dia", 105, yGraficoTitulo, { align: "center" });

      // === GRÁFICO EMPILHADO ===
      if (fila.dadosGrafico.length > 0) {
        const chartElement = (
          <BarChart width={600} height={300} data={fila.dadosGrafico}>
            <XAxis dataKey="data" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Bar dataKey="atendidas" stackId="a" fill="#52c41a" name="Atendidas" />
            <Bar dataKey="abandonadas" stackId="a" fill="#fa8c16" name="Abandonadas" />
            <Bar dataKey="naoAtendidas" stackId="a" fill="#ff4d4f" name="Não Atendidas" />
          </BarChart>
        );

        root.render(chartElement);
        await new Promise((resolve) => setTimeout(resolve, 600));

        const canvas = await html2canvas(tempDiv);
        const imgData = canvas.toDataURL("image/png");
        doc.addImage(imgData, "PNG", 10, yGraficoTitulo + 5, 180, 80);
      } else {
        doc.setFontSize(11);
        doc.text("Não há dados disponíveis para o gráfico neste período", 105, yGraficoTitulo + 40, { align: "center" });
      }
    }

    root.unmount();
    document.body.removeChild(tempDiv);

    // === RODAPÉ ===
    const totalPages = doc.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      doc.setFontSize(9);
      doc.text(`Página ${i} de ${totalPages}`, 105, 290, { align: "center" });
    }

    // === SALVAR E FECHAR A PÁGINA ===
    setStatusGeracao("concluido");
    doc.save("relatorio_chamadas_de_"+dataInicio +"_a_"+dataFim +".pdf");
    setTimeout(() => window.close(), 1500);
  };

  // Renderização com loading melhorado
  const renderizarConteudo = () => {
    switch (statusGeracao) {
      case "carregando":
        return (
          <div className="flex flex-col items-center justify-center h-screen">
            <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-500 mb-4"></div>
            <h2 className="text-xl font-bold mb-2">Carregando dados da API...</h2>
            <p className="text-gray-500">Aguarde enquanto buscamos as informações das chamadas.</p>
          </div>
        );
      
      case "gerando":
        return (
          <div className="flex flex-col items-center justify-center h-screen">
            <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-green-500 mb-4"></div>
            <h2 className="text-xl font-bold mb-2">Gerando PDF...</h2>
            <p className="text-gray-500">Processando {filasProcessadas.length} filas e criando relatório.</p>
            <div className="mt-4 w-64 bg-gray-200 rounded-full h-2.5">
              <div className="bg-green-500 h-2.5 rounded-full animate-pulse" style={{width: '75%'}}></div>
            </div>
          </div>
        );
      
      case "concluido":
        return (
          <div className="flex flex-col items-center justify-center h-screen">
            <div className="text-green-500 text-4xl mb-4">✓</div>
            <h2 className="text-xl font-bold mb-2">PDF Gerado com Sucesso!</h2>
            <p className="text-gray-500">O download será iniciado automaticamente.</p>
          </div>
        );
      
      case "erro":
        return (
          <div className="flex flex-col items-center justify-center h-screen">
            <div className="text-red-500 text-4xl mb-4">✗</div>
            <h2 className="text-xl font-bold mb-2">Erro ao Gerar Relatório</h2>
            <p className="text-gray-500">Ocorreu um erro ao carregar os dados da API.</p>
          </div>
        );
      
      default:
        return (
          <div className="flex flex-col items-center justify-center h-screen">
            <h2 className="text-xl font-bold mb-2">Preparando relatório...</h2>
            <p className="text-gray-500">Iniciando processo de geração do PDF.</p>
          </div>
        );
    }
  };

  return renderizarConteudo();
};

export default RelatorioPDF;